import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { FormControl, Autocomplete, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  getEmployeeDetailsByUuid,
  updateEmployeeDetail,
  saveEmployeeCV,
  downloadEmployeeCV,
  employeeCVExists,
} from "services/api";
import LoadingScreen from "components/Loading";
import IdentityPersonalInfo from "./components/IdentityPersonalInfo";
import { JobForm, PositionForm } from "./components/JobEmploymentInfo";
import ContactInfo from "./components/ContactInfo";
import CompensationEducationForm from "./components/CompensationEducationInfo";

const EmployeeDetails = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const { employees, employeeId } = location.state || {};
  const [selectedEmpId, setSelectedEmpId] = useState(location.state?.employeeId);
  const [empDetails, setEmpDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState({});
  const [error, setError] = useState(null);
  const snackbarRef = useRef();
  const [cvExists, setCvExists] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  useEffect(() => {
    if (!selectedEmpId) return; // don't run if no UUID

    const fetchEmployeeDetails = async () => {
      setLoading(true);
      try {
        const empDetails = await getEmployeeDetailsByUuid(selectedEmpId);
        setEmpDetails(empDetails);
        const exists = await employeeCVExists(selectedEmpId);
        setCvExists(exists);
      } catch (err) {
        console.error("Failed to fetch employee details:", err);
        setError("Failed to fetch employee details");
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeeDetails();
  }, [selectedEmpId]); // runs when employeeUuid changes or component mounts

  const selectedEmployee = useMemo(() => {
    return employees?.find((e) => e.id === selectedEmpId);
  }, [selectedEmpId, employees]);

  const handleEmployeeFieldSave = async (id, field, updatedFieldValue) => {
    try {
      // Build the DTO by merging updated field(s) into existing employee details
      const dto = {
        ...empDetails, // full current employee details
        ...updatedFieldValue, // only the field(s) being updated, must match DTO keys
      };

      // Call the API to update the employee
      const res = await updateEmployeeDetail(id, dto);

      if (res !== "error") {
        // Update local state for immediate UI feedback
        setEmpDetails((prev) => ({ ...prev, ...updatedFieldValue }));

        // Optionally, you could mark this field as saved in your saveStatus
        setSaveStatus((prev) => ({ ...prev, [field]: "success" }));

        return true; // ✅ Save succeeded
      } else {
        // Save failed: mark field with error
        setSaveStatus((prev) => ({ ...prev, [field]: "error" }));
        return false;
      }
    } catch (err) {
      // Exception during save
      setSaveStatus((prev) => ({ ...prev, [field]: "error" }));
      return false;
    } finally {
      // Reset the field save status after a short delay
      setTimeout(() => {
        setSaveStatus((prev) => ({ ...prev, [field]: null }));
      }, 300);
    }
  };

  const handleUploadCV = async (file) => {
    if (!selectedEmpId || !file) return "error";

    try {
      const res = await saveEmployeeCV({ employeeId: selectedEmpId, file });
      if (res !== "error") {
        setCvExists(true);
        // Keep filename in local state to show next to icon
        // setCvFileName(file.name);

        snackbarRef.current?.showSnackbar(
          t("admin.employees.employeeDialog.employeeDetails.cvUploaded"),
          "success"
        );
        return res;
      } else {
        snackbarRef.current?.showSnackbar(
          t("admin.employees.employeeDialog.employeeDetails.cvFailed"),
          "error"
        );
        return "error";
      }
    } catch (err) {
      // snackbarRef.current?.showSnackbar("Failed to upload CV", "error");
      return "error";
    }
  };

  // Parent component
  // const handleDownloadCV = async (employeeId, employeeName) => {
  //   const result = await downloadEmployeeCV(employeeId, employeeName);
  //   if (result.success) {
  //   } else {
  //     console.error("Failed to download CV");
  //   }
  // };

  const handleDownloadCV = async (employeeId, employeeName) => {
    if (!employeeId || !employeeName) return;
    setDownloadLoading(true); // start loading
    try {
      const result = await downloadEmployeeCV(employeeId, employeeName);
      if (result.success) {
      } else {
        console.error("Failed to download CV");
      }
    } finally {
      setDownloadLoading(false); // stop loading
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <>
      <FormControl fullWidth>
        <Autocomplete
          id="employee-autocomplete"
          options={employees}
          getOptionLabel={(option) => option.name || ""}
          value={employees?.find((emp) => emp.id === selectedEmpId) || null}
          onChange={(event, newValue) => {
            setSelectedEmpId(newValue?.id || "");
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("admin.employees.employeeDialog.employeeDetails.selectEmp")}
              variant="outlined"
            />
          )}
        />
      </FormControl>

      <IdentityPersonalInfo
        employeeDetails={empDetails}
        setEmployeeDetails={setEmpDetails}
        saveStatus={saveStatus}
        handleFieldSave={handleEmployeeFieldSave}
        handleUploadCV={handleUploadCV}
        handleDownloadCV={handleDownloadCV}
        employeeName={selectedEmployee?.name}
        selectedEmployeeId={selectedEmpId}
        cvExists={cvExists}
        downloadLoading={downloadLoading}
      />

      <JobForm
        employeeDetails={empDetails}
        setEmployeeDetails={setEmpDetails}
        saveStatus={saveStatus}
        handleFieldSave={handleEmployeeFieldSave}
      />

      <PositionForm
        employeeDetails={empDetails}
        setEmployeeDetails={setEmpDetails}
        saveStatus={saveStatus}
        handleFieldSave={handleEmployeeFieldSave}
      />
      <ContactInfo
        employeeDetails={empDetails}
        setEmployeeDetails={setEmpDetails}
        saveStatus={saveStatus}
        handleFieldSave={handleEmployeeFieldSave}
      />
      <CompensationEducationForm
        employeeDetails={empDetails}
        setEmployeeDetails={setEmpDetails}
        saveStatus={saveStatus}
        handleFieldSave={handleEmployeeFieldSave}
      />
    </>
  );
};

export default EmployeeDetails;
